import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList,
  Zap,
  ArrowRight,
  Sparkles,
  MapPin,
  Calendar as CalIcon,
  DollarSign,
  Video as VideoIcon,
  ShieldCheck,
  Users,
  ChevronRight,
  Volume2,
  VolumeX,
  Mail,
  Rocket,
  Trophy,
  Check,
  Award,
  Compass,
  Wrench,
  ExternalLink,
  FileText,
  ArrowDown,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { VIDEOS } from "@/config/AssetSpine";

// ============================================================
// /blueprint — Sales Victory Walkthrough
// ------------------------------------------------------------
// Rep-facing training node. Four phases:
//   1 · The Gather   → /local diagnostic engine
//   2 · The Wow      → Green Shield entrance (Amanda hero + book)
//   3 · The Spine    → 6-module Owner Command Center
//   4 · The Strike   → Master Strike welcome email (Todd)
//
// Aesthetic: Grit & Gear — weathered kraft/leather/brass.
// Ashley, OH workshop vibe. Every phase has an on-page Rep Note
// card ("How you sell it") + a live link into the real product.
// ============================================================

// ---- Grit & Gear palette -----------------------------------
const G = {
  BG:      "#1A130B",   // aged kraft dark
  BG_HI:   "#241812",
  BG_DEEP: "#0F0A05",
  PAPER:   "#E8DCC4",   // aged clipboard paper
  PAPER_HI:"#D4C4A0",
  INK:     "#2A1F14",   // leather ink
  RUST:    "#B45309",   // rust orange
  RUST_HI: "#F97316",
  LEATHER: "#6B4423",   // medium leather
  BRASS:   "#B8860B",   // weathered brass
  BRASS_HI:"#DAA520",
  CREAM:   "#EAD8B8",   // aged ivory
  MUTED:   "#A08B6E",
};

const PHASES = [
  { n: "01", label: "The Gather", icon: ClipboardList, tone: G.BRASS },
  { n: "02", label: "The Wow",    icon: Sparkles,       tone: G.RUST },
  { n: "03", label: "The Spine",  icon: Compass,        tone: G.BRASS },
  { n: "04", label: "The Strike", icon: Mail,           tone: G.RUST },
];

export default function Blueprint() {
  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ background: G.BG }}>
      <GritBackdrop />

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 space-y-14">
        <Hero />
        <PhaseNav />
        <Phase1Gather />
        <Phase2Wow />
        <Phase3Spine />
        <Phase4Strike />
        <ClosingBanner />
      </main>
    </div>
  );
}

// ============================================================
// Backdrop — kraft grain + vignette
// ============================================================
function GritBackdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <div
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(1100px 700px at 15% -10%, ${G.LEATHER}55, transparent 60%),` +
            `radial-gradient(900px 500px at 100% 20%, ${G.RUST}22, transparent 60%),` +
            `linear-gradient(180deg, ${G.BG_DEEP} 0%, ${G.BG_HI} 55%, ${G.BG_DEEP} 100%)`,
        }}
      />
      {/* Kraft paper grain */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.06]" xmlns="http://www.w3.org/2000/svg">
        <filter id="grit-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix values="0 0 0 0 0.85  0 0 0 0 0.75  0 0 0 0 0.55  0 0 0 1 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#grit-noise)" />
      </svg>
      {/* Faint machine hex grid */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            `linear-gradient(${G.BRASS}66 1px, transparent 1px),` +
            `linear-gradient(90deg, ${G.BRASS}66 1px, transparent 1px)`,
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse 1100px 700px at 50% 30%, black 40%, transparent 90%)",
          WebkitMaskImage: "radial-gradient(ellipse 1100px 700px at 50% 30%, black 40%, transparent 90%)",
        }}
      />
      <style>{`
        @keyframes bp-shine { 0%{transform:translateX(-120%)} 100%{transform:translateX(220%)} }
        @keyframes bp-tape-sway { 0%,100%{transform:rotate(-2deg)} 50%{transform:rotate(-1deg)} }
      `}</style>
    </div>
  );
}

// ============================================================
// HERO
// ============================================================
function Hero() {
  return (
    <section data-testid="bp-hero">
      <div
        className="text-[10px] uppercase tracking-[0.32em] font-mono flex items-center gap-2 mb-3"
        style={{ color: G.BRASS_HI }}
      >
        <ClipboardList size={11} /> Sales Victory Walkthrough · Rep-First Playbook
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white mb-4 max-w-4xl">
        This is how you <span style={{ color: G.RUST_HI }} className="italic">win them.</span>
        {" "}Four phases. One system. Rep drives — Amanda closes.
      </h1>
      <p className="text-sm sm:text-base leading-relaxed max-w-3xl" style={{ color: G.CREAM }}>
        You walk in with the clipboard. You show them Amanda alive on the line.
        You unlock their Command Center on-screen. Amanda mails the welcome the
        moment they sign. You leave with the check.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-[0.28em] font-mono border"
          style={{ borderColor: `${G.BRASS}66`, background: `${G.BRASS}14`, color: G.BRASS_HI }}
        >
          <Award size={11} /> Ashley, OH · Field Tested
        </span>
        <span
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-[0.28em] font-mono border"
          style={{ borderColor: `${G.RUST}66`, background: `${G.RUST}14`, color: G.RUST_HI }}
        >
          <Zap size={11} /> Zero Slide Decks · Live Product
        </span>
        <span
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-[0.28em] font-mono border"
          style={{ borderColor: `${G.LEATHER}88`, background: `${G.LEATHER}22`, color: G.CREAM }}
        >
          <Wrench size={11} /> Grit & Gear Operator
        </span>
      </div>
    </section>
  );
}

// ============================================================
// PHASE NAV — sticky-feeling anchor pills
// ============================================================
function PhaseNav() {
  return (
    <nav
      className="rounded-sm border p-3 sm:p-4 flex flex-wrap items-center gap-2 sm:gap-3"
      style={{
        borderColor: `${G.BRASS}44`,
        background: `linear-gradient(90deg, ${G.LEATHER}22 0%, ${G.BG_HI}CC 60%, ${G.RUST}18 100%)`,
      }}
      data-testid="bp-phase-nav"
    >
      {PHASES.map((p) => (
        <a
          key={p.n}
          href={`#phase-${p.n}`}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border text-[10px] uppercase tracking-[0.28em] font-mono transition-transform active:scale-[0.98]"
          style={{
            borderColor: `${p.tone}66`,
            background: `${p.tone}12`,
            color: p.tone,
          }}
          data-testid={`bp-phase-anchor-${p.n}`}
        >
          <p.icon size={11} />
          <span style={{ color: G.CREAM }}>{p.n}</span>
          <span>· {p.label}</span>
        </a>
      ))}
      <span className="ml-auto text-[10px] uppercase tracking-[0.28em] font-mono flex items-center gap-1.5" style={{ color: G.MUTED }}>
        <ArrowDown size={11} /> Scroll to walk it live
      </span>
    </nav>
  );
}

// ============================================================
// PHASE 1 · THE GATHER — /local diagnostic clipboard
// ============================================================
function Phase1Gather() {
  return (
    <PhaseFrame
      id="phase-01"
      n="01"
      title="The Gather"
      subtitle="You walk in with the clipboard already filled out."
      icon={ClipboardList}
      tone={G.BRASS}
      testId="phase-01"
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left · Clipboard visual */}
        <div className="lg:col-span-3">
          <Clipboard />
          <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Link
              to="/local"
              data-testid="bp-open-local"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm text-[11px] uppercase tracking-[0.24em] font-mono transition-transform active:scale-[0.98]"
              style={{
                background: `linear-gradient(90deg, ${G.LEATHER} 0%, ${G.BRASS} 100%)`,
                border: `1px solid ${G.BRASS}`,
                color: G.INK,
                boxShadow: `0 0 22px -6px ${G.BRASS}`,
              }}
            >
              <ClipboardList size={13} /> Open the Diagnostic Engine <ExternalLink size={12} />
            </Link>
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono flex items-center gap-2" style={{ color: G.MUTED }}>
              <MapPin size={11} style={{ color: G.RUST_HI }} />
              /local · Amanda scores their business before you knock
            </div>
          </div>
        </div>

        {/* Right · Rep Notes */}
        <RepNote
          testId="bp-rep-note-01"
          items={[
            { k: "Say this", v: "\"I already pulled your public files — reviews, hours, response times. Give me two minutes.\"" },
            { k: "Show them", v: "The health score. Point at the red boxes. That is the emotional hook." },
            { k: "Never say", v: "\"AI\", \"platform\", or \"software\". Say \"the utility\" or \"the Grid\"." },
            { k: "Close on", v: "\"Want to see what it looks like when this is fixed? That's Phase 2.\"" },
          ]}
        />
      </div>
    </PhaseFrame>
  );
}

function Clipboard() {
  return (
    <div
      className="relative rounded-sm p-2 sm:p-3"
      style={{
        background: `linear-gradient(160deg, ${G.LEATHER} 0%, ${G.INK} 100%)`,
        boxShadow: `0 30px 70px -20px rgba(0,0,0,0.8), 0 0 0 1px ${G.BRASS}44 inset`,
      }}
      data-testid="bp-clipboard"
    >
      {/* Clipboard clip */}
      <div className="flex items-center justify-center mb-2">
        <div
          className="h-4 w-16 rounded-t-sm"
          style={{
            background: `linear-gradient(180deg, ${G.BRASS_HI} 0%, ${G.BRASS} 60%, ${G.LEATHER} 100%)`,
            boxShadow: `0 4px 10px rgba(0,0,0,0.5), 0 0 0 1px ${G.BRASS_HI}66 inset`,
          }}
        />
      </div>

      {/* Paper */}
      <div
        className="relative rounded-sm p-5 sm:p-6"
        style={{
          background: `linear-gradient(180deg, ${G.PAPER} 0%, ${G.PAPER_HI} 100%)`,
          color: G.INK,
          minHeight: 380,
        }}
      >
        {/* Paper grain */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.14] pointer-events-none rounded-sm" xmlns="http://www.w3.org/2000/svg">
          <filter id="paper-grit">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" />
            <feColorMatrix values="0 0 0 0 0.4  0 0 0 0 0.3  0 0 0 0 0.1  0 0 0 0.8 0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#paper-grit)" />
        </svg>

        {/* Corner stamp */}
        <div
          className="absolute top-3 right-3 border-2 rounded-sm px-2 py-1 text-[9px] font-mono uppercase tracking-[0.24em]"
          style={{ borderColor: G.RUST, color: G.RUST, transform: "rotate(6deg)" }}
        >
          Live Scan · Amanda
        </div>

        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.32em] font-mono mb-1" style={{ color: G.LEATHER }}>
            /local · Diagnostic Engine
          </div>
          <div className="font-display text-2xl sm:text-3xl leading-tight" style={{ color: G.INK }}>
            Todd&apos;s Auto Repair
          </div>
          <div className="text-[11px] font-mono mt-1" style={{ color: G.LEATHER }}>
            123 Main St · Ashley, OH · Since 1994
          </div>

          {/* Health score row */}
          <div className="mt-5 grid grid-cols-3 gap-3">
            {[
              { k: "Missed calls / wk",  v: "17",  bad: true },
              { k: "Avg reply time",     v: "9h",  bad: true },
              { k: "Google score",       v: "3.9", bad: true },
            ].map((s) => (
              <div key={s.k} className="rounded-sm border p-3" style={{ borderColor: G.RUST, background: `${G.RUST}18` }}>
                <div className="font-display text-2xl leading-none" style={{ color: G.RUST }}>{s.v}</div>
                <div className="text-[9px] font-mono uppercase tracking-[0.22em] mt-1" style={{ color: G.LEATHER }}>{s.k}</div>
              </div>
            ))}
          </div>

          {/* Handwritten-feel checklist */}
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono mb-2" style={{ color: G.LEATHER }}>
              Field notes · Amanda&apos;s watch
            </div>
            <ul className="space-y-1.5 text-[13px] leading-snug" style={{ color: G.INK, fontFamily: "'Kalam', cursive, ui-sans-serif" }}>
              <li>· Phone rings 40+ min after hours — nobody picks it up.</li>
              <li>· Reviews stopped in Sept. Owner probably stopped asking.</li>
              <li>· Website form is a graveyard. Zero follow-up template.</li>
              <li>· Facebook page went dark 2023. Free ad real estate.</li>
            </ul>
          </div>

          {/* Signature line */}
          <div className="mt-6 pt-4 border-t flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.22em]" style={{ borderColor: `${G.LEATHER}44`, color: G.LEATHER }}>
            <span>Rep · <span style={{ color: G.RUST }}>Field-Verified</span></span>
            <span>Delivery ETA · <span style={{ color: G.RUST }}>Today, 3 pm</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PHASE 2 · THE WOW — Amanda hero + booking slot preview
// ============================================================
function Phase2Wow() {
  return (
    <PhaseFrame
      id="phase-02"
      n="02"
      title="The Wow"
      subtitle="You hand them the tablet. Amanda speaks. Their booking calendar comes alive."
      icon={Sparkles}
      tone={G.RUST}
      testId="phase-02"
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left · Amanda hero */}
        <div className="lg:col-span-3">
          <AmandaFramed />
          <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Link
              to="/green-shield"
              data-testid="bp-open-shield"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm text-[11px] uppercase tracking-[0.24em] font-mono transition-transform active:scale-[0.98]"
              style={{
                background: `linear-gradient(90deg, ${G.RUST} 0%, ${G.RUST_HI} 100%)`,
                border: `1px solid ${G.RUST_HI}`,
                color: G.INK,
                boxShadow: `0 0 22px -6px ${G.RUST_HI}`,
              }}
            >
              <ShieldCheck size={13} /> Open the Green Shield Entrance <ExternalLink size={12} />
            </Link>
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono flex items-center gap-2" style={{ color: G.MUTED }}>
              <VideoIcon size={11} style={{ color: G.BRASS_HI }} /> Amanda 4.0 · Founder Greeting · 90 sec
            </div>
          </div>
        </div>

        {/* Right · booking slot preview + rep notes */}
        <div className="lg:col-span-2 space-y-4">
          <BookingSlotPreview />
          <RepNote
            testId="bp-rep-note-02"
            items={[
              { k: "Say this", v: "\"That's not a video actor. That's the person answering your phone tonight.\"" },
              { k: "Do this",  v: "Tap an open slot. Show the request confirmation land in the calendar." },
              { k: "Watch for",v: "The exhale. The lean-in. When they touch the screen — they're closed." },
            ]}
            compact
          />
        </div>
      </div>
    </PhaseFrame>
  );
}

function AmandaFramed() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <div className="relative mx-auto w-full" data-testid="bp-amanda">
      <div
        className="absolute -inset-1 rounded-sm pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${G.BRASS}55, ${G.RUST}55, ${G.BRASS}55)`,
          filter: "blur(18px)",
          opacity: 0.55,
        }}
      />
      <div
        className="relative rounded-sm overflow-hidden border"
        style={{
          borderColor: `${G.BRASS}88`,
          boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 60px ${G.RUST}33`,
          background: "#000",
        }}
      >
        <video
          ref={videoRef}
          src={VIDEOS.AMANDA_FOUNDER}
          autoPlay
          muted
          playsInline
          controls
          data-testid="bp-amanda-video-el"
          className="w-full h-auto block bg-black"
        />
        <button
          type="button"
          onClick={toggle}
          data-testid="bp-amanda-mute"
          className="absolute top-3 right-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-sm backdrop-blur transition-colors"
          style={{
            background: `${G.INK}CC`,
            border: `1px solid ${G.BRASS}66`,
            color: G.BRASS_HI,
          }}
        >
          {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
          <span className="text-[10px] uppercase tracking-[0.22em] font-mono">
            {muted ? "Tap to unmute" : "Muted off"}
          </span>
        </button>
        <div
          className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-sm backdrop-blur border"
          style={{ background: `${G.INK}CC`, borderColor: `${G.RUST}66` }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: G.RUST_HI, boxShadow: `0 0 8px ${G.RUST_HI}` }}
          />
          <span className="text-[9px] uppercase tracking-[0.22em] font-mono" style={{ color: G.CREAM }}>
            Amanda · Live · Founder Node
          </span>
        </div>
      </div>
    </div>
  );
}

function BookingSlotPreview() {
  return (
    <div
      className="rounded-sm border overflow-hidden"
      style={{
        borderColor: `${G.BRASS}66`,
        background: `linear-gradient(180deg, ${G.LEATHER}18 0%, ${G.BG_HI}E6 100%)`,
      }}
      data-testid="bp-booking-preview"
    >
      <div
        className="px-4 py-3 border-b flex items-center gap-3"
        style={{ borderColor: "rgba(255,255,255,0.06)", background: `${G.BRASS}12` }}
      >
        <div
          className="w-8 h-8 rounded-sm border flex items-center justify-center"
          style={{ borderColor: `${G.BRASS}88`, background: `${G.BRASS}22`, color: G.BRASS_HI }}
        >
          <CalIcon size={14} />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: G.BRASS_HI }}>
            The Booking Calendar
          </div>
          <div className="font-display text-white text-sm leading-tight">Book This Slot · Live Demo</div>
        </div>
      </div>

      <div className="p-4 space-y-2">
        {[
          { day: "Tue",  time: "10 am", state: "requested", label: "Requested · Amanda confirming" },
          { day: "Wed",  time: "12 pm", state: "booked",    label: "Crew A · Booked" },
          { day: "Thu",  time: "10 am", state: "open",      label: "Open · Book This Slot" },
          { day: "Fri",  time: "8 am",  state: "booked",    label: "Crew B · Booked" },
        ].map((s, i) => {
          const tone = s.state === "booked" ? G.BRASS : s.state === "requested" ? G.RUST_HI : G.CREAM;
          const bg =
            s.state === "booked" ? `${G.BRASS}22` :
            s.state === "requested" ? `${G.RUST_HI}22` : `${G.LEATHER}22`;
          return (
            <div
              key={i}
              className="rounded-sm border px-3 py-2 flex items-center justify-between"
              style={{ borderColor: `${tone}66`, background: bg }}
              data-testid={`bp-slot-${s.state}-${i}`}
            >
              <div className="text-[10px] uppercase tracking-[0.24em] font-mono" style={{ color: tone }}>
                {s.day} · {s.time}
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-mono flex items-center gap-1" style={{ color: G.CREAM }}>
                {s.state === "requested" && <Check size={10} />}
                {s.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// PHASE 3 · THE SPINE — 6-module Owner Command Center
// ============================================================
const SPINE_MODULES = [
  { n: "01", label: "Intake Shield",     icon: ShieldCheck, blurb: "Every call captured. Every callback booked.",     tone: G.RUST_HI },
  { n: "02", label: "Command Calendar",  icon: CalIcon,     blurb: "Live dispatch board. Crews already staged at dawn.",tone: G.BRASS_HI },
  { n: "03", label: "Utility Spine",     icon: ClipboardList, blurb: "Every client, every quirk, remembered on file.",   tone: G.RUST_HI },
  { n: "04", label: "Billing Autopilot", icon: DollarSign,  blurb: "Job done → invoice sent. One button, one tap.",     tone: G.BRASS_HI },
  { n: "05", label: "Growth Engine",     icon: VideoIcon,   blurb: "Ten videos a week. Amanda drafts, you approve.",     tone: G.RUST_HI },
  { n: "06", label: "Founder Node",      icon: Trophy,      blurb: "1-2-12 lifetime rate. $0 → $97 → $297 · locked.",    tone: G.BRASS_HI },
];

function Phase3Spine() {
  return (
    <PhaseFrame
      id="phase-03"
      n="03"
      title="The Spine"
      subtitle="Six modules. One Command Center. This is what they walk into on day two."
      icon={Compass}
      tone={G.BRASS}
      testId="phase-03"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {SPINE_MODULES.map((m) => (
          <ModuleCard key={m.n} mod={m} />
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-sm border p-4 flex items-start gap-3"
          style={{ borderColor: `${G.BRASS}44`, background: `${G.BRASS}0F` }}
        >
          <Sparkles size={16} style={{ color: G.BRASS_HI }} className="mt-0.5" />
          <div className="text-[12px] leading-relaxed" style={{ color: G.CREAM }}>
            <span className="uppercase tracking-[0.22em] font-mono mr-2" style={{ color: G.BRASS_HI }}>Command Center ·</span>
            The owner logs in one time and every part of their operation is
            already staged — calls answered, calendar filled, crews notified,
            invoices firing, videos posting. You&apos;re not selling software. You&apos;re
            selling <span style={{ color: G.RUST_HI }} className="italic">the day off they haven&apos;t had in ten years.</span>
          </div>
        </div>

        <Link
          to="/green-shield"
          data-testid="bp-open-cockpit"
          className="rounded-sm border p-4 flex items-center gap-3 group transition-transform active:scale-[0.98]"
          style={{
            borderColor: `${G.RUST}88`,
            background: `linear-gradient(90deg, ${G.RUST}22 0%, ${G.BG_HI}CC 100%)`,
            boxShadow: `0 0 22px -8px ${G.RUST}`,
          }}
        >
          <Compass size={22} style={{ color: G.RUST_HI }} />
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: G.RUST_HI }}>Rep Action</div>
            <div className="font-display text-white text-sm leading-tight">Walk Them Through the Live Cockpit</div>
          </div>
          <ArrowRight size={16} style={{ color: G.CREAM }} className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </PhaseFrame>
  );
}

function ModuleCard({ mod }) {
  const Icon = mod.icon;
  return (
    <div
      className="rounded-sm border p-4 relative overflow-hidden"
      style={{
        borderColor: `${mod.tone}44`,
        background: `linear-gradient(160deg, ${mod.tone}12 0%, ${G.BG_HI}E6 65%)`,
      }}
      data-testid={`bp-module-${mod.n}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-sm border flex items-center justify-center shrink-0"
          style={{ borderColor: `${mod.tone}66`, background: `${mod.tone}18`, color: mod.tone }}
        >
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: mod.tone }}>Module {mod.n}</div>
          <div className="font-display text-white text-base leading-tight">{mod.label}</div>
        </div>
      </div>
      <p className="mt-3 text-[12px] leading-relaxed" style={{ color: G.CREAM }}>{mod.blurb}</p>
      <div className="mt-3 text-[10px] uppercase tracking-[0.22em] font-mono flex items-center gap-1.5" style={{ color: mod.tone }}>
        Unlocked <ChevronRight size={11} />
      </div>
    </div>
  );
}

// ============================================================
// PHASE 4 · THE STRIKE — Todd's welcome email
// ============================================================
function Phase4Strike() {
  return (
    <PhaseFrame
      id="phase-04"
      n="04"
      title="The Strike"
      subtitle="Todd signs. Amanda fires this from ashes@wingmanalfred.com before he's back to his truck."
      icon={Mail}
      tone={G.RUST}
      testId="phase-04"
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left · Email mock */}
        <div className="lg:col-span-3">
          <StrikeEmail />
        </div>

        {/* Right · Rep notes */}
        <div className="lg:col-span-2 space-y-4">
          <RepNote
            testId="bp-rep-note-04"
            items={[
              { k: "Trigger", v: "Fires the instant Todd signs the Founder Terms — 0s delay." },
              { k: "Copy",    v: "Written by Wingman, tuned by Amanda. Personalized on <first_name> + <trade>." },
              { k: "Reply-to",v: "Goes to Amanda's inbox, not yours. She handles the follow-through." },
              { k: "Say this",v: "\"Watch your phone. My tech partner will have your welcome landed before you're back to the shop.\"" },
            ]}
          />
          <div
            className="rounded-sm border p-4"
            style={{ borderColor: `${G.RUST}66`, background: `linear-gradient(90deg, ${G.RUST}22 0%, ${G.BG_HI}CC 100%)` }}
          >
            <div className="text-[10px] uppercase tracking-[0.32em] font-mono flex items-center gap-2 mb-2" style={{ color: G.RUST_HI }}>
              <Rocket size={11} /> Master Strike · Live
            </div>
            <div className="text-[12px] leading-relaxed" style={{ color: G.CREAM }}>
              You just closed one Founder Node. Amanda takes it from here — she
              onboards, she schedules the setup call, she loads the calendar
              from the /local scan you already ran in Phase 1.
            </div>
          </div>
        </div>
      </div>
    </PhaseFrame>
  );
}

function StrikeEmail() {
  return (
    <div
      className="rounded-sm overflow-hidden border relative"
      style={{
        borderColor: `${G.BRASS}66`,
        background: G.PAPER,
        color: G.INK,
        boxShadow: `0 30px 80px -20px rgba(0,0,0,0.7)`,
      }}
      data-testid="bp-strike-email"
    >
      {/* Envelope tape strip */}
      <div
        className="absolute -top-3 left-8 h-7 w-32 rotate-[-3deg] opacity-90 z-10"
        style={{
          background: `repeating-linear-gradient(45deg, ${G.BRASS}88 0 6px, ${G.BRASS_HI}88 6px 12px)`,
          boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
        }}
      />

      {/* Header bar */}
      <div
        className="px-5 py-3 border-b flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.24em]"
        style={{ borderColor: `${G.LEATHER}44`, background: `linear-gradient(180deg, ${G.PAPER_HI} 0%, ${G.PAPER} 100%)`, color: G.LEATHER }}
      >
        <div className="flex items-center gap-2">
          <Mail size={13} style={{ color: G.RUST }} />
          <span>Wingman Alfred · Outbound Grid</span>
        </div>
        <span style={{ color: G.RUST }}>DELIVERED · 00:04 s after sign</span>
      </div>

      {/* Meta */}
      <div className="px-5 sm:px-7 py-4 border-b space-y-1 text-[12px] font-mono" style={{ borderColor: `${G.LEATHER}22` }}>
        <div><span style={{ color: G.LEATHER }}>From:</span> <span style={{ color: G.INK }}>Amanda · Wingman Alfred &lt;amanda@wingmanalfred.com&gt;</span></div>
        <div><span style={{ color: G.LEATHER }}>To:</span> <span style={{ color: G.INK }}>Todd Bennett &lt;todd@toddsautorepair.com&gt;</span></div>
        <div><span style={{ color: G.LEATHER }}>Subject:</span> <span style={{ color: G.RUST }}>Welcome to the Grid, Todd — your Founder Node is live.</span></div>
      </div>

      {/* Body */}
      <div className="px-5 sm:px-7 py-6 space-y-4 text-[14px] leading-relaxed" style={{ color: G.INK, fontFamily: "'Manrope', ui-sans-serif" }}>
        <div className="font-display text-2xl sm:text-3xl leading-tight" style={{ color: G.INK }}>
          Todd — you&apos;re in. Welcome to the <span style={{ color: G.RUST }} className="italic">Grid</span>.
        </div>

        <p>
          The moment you signed, three things happened on our side.
        </p>

        <ol className="pl-5 list-decimal space-y-2">
          <li><span style={{ color: G.RUST, fontWeight: 600 }}>Your line went live.</span> I&apos;m already answering inbound calls to (614) 555-0177. Every missed call, every callback, filed on the Grid.</li>
          <li><span style={{ color: G.RUST, fontWeight: 600 }}>Your calendar is loaded.</span> Every open bay for the next 14 days is on the Booking Calendar. Riders can request slots from your website tonight.</li>
          <li><span style={{ color: G.RUST, fontWeight: 600 }}>Your setup call is booked.</span> Tomorrow · 10 am · with Wingman. Fifteen minutes. We wire up your Google Reviews auto-loop and turn on Billing Autopilot.</li>
        </ol>

        <div
          className="rounded-sm border-l-4 pl-4 py-3 my-2"
          style={{ borderColor: G.RUST, background: `${G.RUST}0F` }}
        >
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono mb-1" style={{ color: G.RUST }}>The 1-2-12 · Locked</div>
          <div className="text-[13px]">
            Month 1 · <span style={{ color: G.RUST, fontWeight: 600 }}>$0</span> · we build it.
            Month 2 · <span style={{ color: G.RUST, fontWeight: 600 }}>$97</span> · you soft-launch.
            Month 12 forward · <span style={{ color: G.RUST, fontWeight: 600 }}>$297/mo</span>. That rate never rises.
          </div>
        </div>

        <p>
          Reply to this email with anything — a question, a photo, a new
          service to add to your intake. I read every one. I answer within the hour.
        </p>

        <p style={{ color: G.LEATHER }}>
          — Amanda<br />
          <span className="text-[11px] font-mono uppercase tracking-[0.22em]">Wingman Alfred · Last 50 Mile Grid · Ashley, OH</span>
        </p>

        {/* CTA button */}
        <div className="pt-2">
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            data-testid="bp-strike-email-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-sm text-[11px] uppercase tracking-[0.24em] font-mono"
            style={{
              background: `linear-gradient(90deg, ${G.RUST} 0%, ${G.RUST_HI} 100%)`,
              border: `1px solid ${G.RUST_HI}`,
              color: G.PAPER,
              boxShadow: `0 0 22px -8px ${G.RUST_HI}`,
            }}
          >
            <ShieldCheck size={13} /> Open My Command Center <ArrowRight size={13} />
          </a>
        </div>
      </div>

      {/* Footer */}
      <div
        className="px-5 py-3 border-t text-[10px] font-mono uppercase tracking-[0.24em] flex items-center justify-between"
        style={{ borderColor: `${G.LEATHER}22`, background: `${G.PAPER_HI}`, color: G.LEATHER }}
      >
        <span>Sent by Amanda · Wingman Alfred</span>
        <span>Reply STOP to opt out</span>
      </div>
    </div>
  );
}

// ============================================================
// CLOSING BANNER
// ============================================================
function ClosingBanner() {
  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${G.BRASS}88`,
        background: `linear-gradient(160deg, ${G.RUST}22 0%, ${G.BG_HI}D9 45%, ${G.BRASS}18 100%)`,
        boxShadow: `0 40px 100px -30px ${G.BRASS}66, 0 0 0 1px ${G.BRASS}22 inset`,
      }}
      data-testid="bp-closing-banner"
    >
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${G.RUST_HI}, ${G.BRASS_HI}, ${G.RUST_HI}, transparent)` }} />
      <div
        className="absolute inset-y-0 w-40 pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, ${G.BRASS}22, transparent)`,
          animation: "bp-shine 5s ease-in-out infinite",
        }}
      />
      <div className="relative p-6 sm:p-10">
        <div className="text-[10px] uppercase tracking-[0.36em] font-mono flex items-center gap-2 mb-3" style={{ color: G.BRASS_HI }}>
          <Trophy size={12} /> The Rep Wins When
        </div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white leading-tight max-w-3xl">
          You <span style={{ color: G.RUST_HI }} className="italic">carry the clipboard.</span> Amanda
          carries the follow-through. The owner carries the day off.
        </h2>
        <p className="mt-3 text-sm sm:text-base leading-relaxed max-w-3xl" style={{ color: G.CREAM }}>
          Four phases. One system. If you got them past Phase 2, you got them.
          If you got them to Phase 4, they&apos;re already on the Grid.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <Link
            to="/local"
            data-testid="bp-closing-cta-local"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm text-sm font-display uppercase tracking-[0.24em] transition-transform active:scale-[0.99]"
            style={{
              background: `linear-gradient(90deg, ${G.RUST} 0%, ${G.RUST_HI} 100%)`,
              border: `1px solid ${G.RUST_HI}`, color: G.INK,
              boxShadow: `0 0 32px -6px ${G.RUST_HI}`,
            }}
          >
            <ClipboardList size={15} /> Run My First /local Scan <ArrowRight size={15} />
          </Link>
          <Link
            to="/green-shield"
            data-testid="bp-closing-cta-shield"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm text-sm font-display uppercase tracking-[0.24em] border transition-transform active:scale-[0.99]"
            style={{
              borderColor: `${G.BRASS}88`,
              background: `${G.BRASS}18`,
              color: G.BRASS_HI,
            }}
          >
            <ShieldCheck size={15} /> Preview the Green Shield Entrance
          </Link>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Shared: Phase frame + rep note card
// ============================================================
function PhaseFrame({ id, n, title, subtitle, icon: Icon, tone, testId, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={testId}>
      <div
        className="rounded-sm border overflow-hidden relative"
        style={{
          borderColor: `${tone}55`,
          background: `linear-gradient(180deg, ${tone}0D 0%, ${G.BG_HI}F2 100%)`,
        }}
      >
        {/* Banner */}
        <div
          className="px-5 sm:px-6 py-5 flex items-center gap-4 border-b relative"
          style={{
            borderColor: "rgba(255,255,255,0.06)",
            background: `linear-gradient(90deg, ${tone}18 0%, ${G.LEATHER}22 60%, ${tone}10 100%)`,
          }}
        >
          <div
            className="w-12 h-12 rounded-sm border flex items-center justify-center shrink-0 relative"
            style={{ borderColor: `${tone}88`, background: `${tone}22`, color: tone }}
          >
            <Icon size={22} />
            <span
              className="absolute -top-2 -left-2 px-1.5 py-0.5 rounded-sm text-[8px] font-mono uppercase tracking-[0.28em] border"
              style={{ background: G.BG, borderColor: `${tone}88`, color: tone }}
            >
              Phase {n}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] font-mono" style={{ color: tone }}>
              Phase {n} · {title}
            </div>
            <div className="font-display text-white text-lg sm:text-xl leading-tight mt-0.5">{subtitle}</div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-6">{children}</div>
      </div>
    </section>
  );
}

function RepNote({ items, testId, compact = false }) {
  return (
    <div
      className={"rounded-sm relative " + (compact ? "p-4" : "p-5")}
      style={{
        background: `linear-gradient(180deg, ${G.PAPER} 0%, ${G.PAPER_HI} 100%)`,
        color: G.INK,
        boxShadow: `0 20px 50px -15px rgba(0,0,0,0.55), 0 0 0 1px ${G.LEATHER}44 inset`,
        transform: "rotate(-0.6deg)",
      }}
      data-testid={testId}
    >
      {/* Tape strip */}
      <div
        className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 w-24 opacity-90"
        style={{
          background: `repeating-linear-gradient(45deg, ${G.BRASS}88 0 4px, ${G.BRASS_HI}88 4px 8px)`,
          transform: "translate(-50%, 0) rotate(-2deg)",
          boxShadow: "0 3px 10px rgba(0,0,0,0.3)",
        }}
      />

      <div className="text-[10px] uppercase tracking-[0.32em] font-mono mb-3 flex items-center gap-2" style={{ color: G.RUST }}>
        <FileText size={11} /> Rep Note · How You Sell It
      </div>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={i}>
            <div className="text-[10px] uppercase tracking-[0.24em] font-mono" style={{ color: G.LEATHER }}>{it.k}</div>
            <div className={"text-[13px] leading-snug mt-0.5 " + (compact ? "" : "sm:text-[13.5px]")} style={{ color: G.INK }}>
              {it.v}
            </div>
          </li>
        ))}
      </ul>

      {/* Bottom signature */}
      <div className="mt-4 pt-3 border-t flex items-center gap-2 text-[9px] font-mono uppercase tracking-[0.24em]" style={{ borderColor: `${G.LEATHER}33`, color: G.LEATHER }}>
        <Users size={10} style={{ color: G.RUST }} /> Rep-First · Grit & Gear · Field-Tested
      </div>
    </div>
  );
}
